import {
  ActivityFeedItemFragment,
  ActivityRelativeTypeEnum,
  ActivityTypeEnum,
} from "../schema";

export type GranularCardTypename = "Order" | "PurchaseRequest" | "Rfx";

export type ActivityGranularTarget =
  | { kind: "granular"; typename: GranularCardTypename; id: string }
  | { kind: "columns" };

// Activity types that only change an EXISTING card's data while it stays in its
// column. Everything else (create / drag / received-quantities / invoice-from-
// order, item-relative changes, unknown types) falls back to a column refetch,
// which also handles the adds / removes / moves a single-card patch cannot.
const IN_PLACE_TYPES: ReadonlySet<ActivityTypeEnum> = new Set([
  ActivityTypeEnum.ASSIGN,
  ActivityTypeEnum.UPDATE_STATUS,
  ActivityTypeEnum.CARD_UPDATED,
  ActivityTypeEnum.CREATE_APPROVAL_WORKFLOW,
  ActivityTypeEnum.UPDATE_APPROVAL_WORKFLOW,
  ActivityTypeEnum.CREATE_APPROVAL_REQUEST,
  ActivityTypeEnum.UPDATE_APPROVAL_REQUEST,
  ActivityTypeEnum.ADD_ATTACHMENT,
]);

const RELATIVE_TYPE_TO_CARD: Partial<
  Record<ActivityRelativeTypeEnum, GranularCardTypename>
> = {
  [ActivityRelativeTypeEnum.PURCHASE_REQUEST]: "PurchaseRequest",
  [ActivityRelativeTypeEnum.RFX]: "Rfx",
  [ActivityRelativeTypeEnum.ORDER]: "Order",
};

/**
 * Decide whether a live activity can be applied by fetching just the changed
 * card (granular) or requires refetching the affected column(s).
 *
 * Granular is only used for in-place updates to a card that stays in its column:
 * PR and RFX each occupy a single column, and an order status/assign change
 * never alters the order's `type` (which is what decides its column). If the
 * subsequent single-card fetch returns null (archived / removed), the caller
 * falls back to a column refetch. Item-relative activities carry the item id in
 * `relativeId` (not the card id), so they stay on the column-refetch path.
 */
export function getActivityGranularTarget(
  activity: ActivityFeedItemFragment | undefined,
): ActivityGranularTarget {
  if (!activity || !activity.relativeId) {
    return { kind: "columns" };
  }

  if (!IN_PLACE_TYPES.has(activity.type)) {
    return { kind: "columns" };
  }

  const typename = RELATIVE_TYPE_TO_CARD[activity.relativeType];

  if (!typename) {
    return { kind: "columns" };
  }

  return { kind: "granular", typename, id: activity.relativeId };
}
